import { FormControl, FormHelperText, InputLabel, MenuItem, Select } from "@mui/material";

import { getFieldErrorMessage, useFieldContext, useShowFieldError } from "./form.context";

type Option = {
  label?: string;
  name?: string;
  value: string | number;
};

type Props = {
  label: string;
  options: Option[];
  /** Allow clearing the value (stores `null`). Needed for nullable enum fields like fileType. */
  clearable?: boolean;
  emptyLabel?: string;
};

export function SelectField({ label, options, clearable = false, emptyLabel = "—" }: Props) {
  const field = useFieldContext<string | number | null | undefined>();
  const errors = field.state.meta.errors;
  const hasError = useShowFieldError(errors);
  const errorMessage = getFieldErrorMessage(errors);
  const raw = field.state.value;
  const isEmpty = raw == null || raw === "";
  // MUI Select needs "" for empty; our schema uses null for "no file type"
  const displayValue = isEmpty ? "" : raw;

  return (
    <FormControl fullWidth size="small" error={hasError}>
      <InputLabel
        id={`select-${field.name}-label`}
        sx={(theme) => ({
          fontWeight: 600,
          color: theme.palette.text.secondary,
          "&.Mui-focused": { color: theme.palette.primary.main },
        })}
      >
        {label}
      </InputLabel>
      <Select
        name={field.name}
        value={displayValue}
        onChange={(e) => {
          const next = e.target.value;
          field.handleChange(next === "" ? null : next);
        }}
        onBlur={field.handleBlur}
        labelId={`select-${field.name}-label`}
        label={label}
      >
        {clearable ? (
          <MenuItem value="">
            <em>{emptyLabel}</em>
          </MenuItem>
        ) : null}
        {options.map((option) => (
          <MenuItem key={`${field.name}-${option.value}`} value={option.value}>
            {option.label ?? option.name}
          </MenuItem>
        ))}
      </Select>
      {hasError && errorMessage ? <FormHelperText>{errorMessage}</FormHelperText> : null}
    </FormControl>
  );
}
